import { CreateCampaignFormValue } from '@features/Admin/Campaign/model/type';
import { Grid } from '@mui/material';
import {
  CampaignFieldsError,
  CampaignRetryFields as CampaignRetryFieldsType,
} from '@shared/types/admin/campaign';
import { Controller, useFormContext } from 'react-hook-form';
import CustomFormLabelV2 from '../forms/theme-elements/CustomFormLabelV2';
import { t } from 'i18next';
import { CounterInput } from '../counterInput/counterInput';
import { StyledErrorText } from '../errorText';

interface CampaignRetryFields {
  fields: {
    name: string;
    label: string;
    placeholder: string;
    retryFieldError: string[];
    retryFieldValue: string;
    setRetryFieldValue: React.Dispatch<React.SetStateAction<string>>;
  }[];
  setCampaignFieldError: React.Dispatch<React.SetStateAction<CampaignFieldsError>>;
}

export interface ChangeRetryValueByButtonData {
  value: number;
  name: string;
}

export const CampaignRetryFields = ({ fields, setCampaignFieldError }: CampaignRetryFields) => {
  const {
    control,
    formState: { errors: formStateErrors },
    setValue,
    getValues,
  } = useFormContext<CreateCampaignFormValue>();

  const applyField = (name: string, value: number) => {
    const field = fields.find((fieldItem) => fieldItem.name === name);

    if (field) {
      field.setRetryFieldValue(String(value));
    }
    setCampaignFieldError((prevState) => ({
      ...prevState,
      [name]: [],
    }));
    setValue(name as CampaignRetryFieldsType, value, { shouldDirty: true });
  };

  const setValueToForm = (name: string, value: string) => {
    const parsedValue = Number.isNaN(parseInt(value, 10)) ? 0 : parseInt(value, 10);

    if (name !== 'retryMaxNumber' && name !== 'retriesPerDay') {
      applyField(name, parsedValue);
      return;
    }

    const currentMax = Number(getValues('retryMaxNumber')) || 0;
    const currentPerDay = Number(getValues('retriesPerDay')) || 0;

    let nextMax = name === 'retryMaxNumber' ? parsedValue : currentMax;
    let nextPerDay = name === 'retriesPerDay' ? parsedValue : currentPerDay;

    if (name === 'retryMaxNumber') {
      if (nextMax <= 0) {
        nextMax = 0;
        nextPerDay = 0;
      } else if (nextPerDay > nextMax) {
        nextPerDay = nextMax;
      } else if (nextPerDay <= 0) {
        nextPerDay = 1;
      }
    } else {
      if (nextPerDay <= 0) {
        nextPerDay = 0;
        nextMax = 0;
      } else if (nextPerDay > nextMax) {
        nextMax = nextPerDay;
      }
    }

    applyField('retryMaxNumber', nextMax);
    applyField('retriesPerDay', nextPerDay);
  };

  const handleOnChangeRetryFieldInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const filteredValue = value.replace(/[^0-9]/g, '');

    setValueToForm(name, filteredValue);
  };

  const handleOnChangeRetryValueByButton = (data: ChangeRetryValueByButtonData) => {
    const { name, value } = data;
    const filteredValue = value < 0 ? '0' : String(value).replace(/[^0-9]/g, '');
    setValueToForm(name, filteredValue);
  };

  return (
    <>
      <Grid container spacing={2} alignItems="flex-start">
        {fields.map(({ name, label }: { name: string; label: string }) => (
          <Grid item xs={12} sm={6} md={4} key={name}>
            <CustomFormLabelV2 htmlFor={`form-field-${name}`}>{t(label)}</CustomFormLabelV2>
            <Controller
              control={control}
              name={name as CampaignRetryFieldsType}
              render={({ field }) => (
                <CounterInput
                  {...field}
                  id={`form-field-${name}`}
                  retryFieldValue={String(field.value || 0)}
                  fieldName={name}
                  control={control}
                  isErrored={Boolean(formStateErrors[name as CampaignRetryFieldsType])}
                  handleOnChangeRetryFieldInput={(e) => {
                    field.onChange(e.target.value);
                    handleOnChangeRetryFieldInput(e);
                  }}
                  handleOnChangeRetryValueByButton={(e) => {
                    field.onChange(e.value);
                    handleOnChangeRetryValueByButton(e);
                  }}
                />
              )}
            />
            {formStateErrors[name as CampaignRetryFieldsType] && (
              <StyledErrorText>{t('adminMainPage.errors.fillTheField')}</StyledErrorText>
            )}
          </Grid>
        ))}
      </Grid>
    </>
  );
};
